/**
 * Token Service - Centralized token management with validation and refresh
 * Handles token expiration detection and automatic refresh
 */

import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { tokenRequest } from "./config";
import { getMsalInstance } from "./msalInstance";
import { store } from "@/vuex/store";

// Token refresh lock to prevent multiple simultaneous refreshes
let isRefreshing = false;
let refreshSubscribers = [];

/**
 * Parse JWT token and extract payload
 */
function parseJwt(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error('[TokenService] Failed to parse JWT:', e);
    return null;
  }
}

/**
 * Check if token is expired or about to expire (within 5 minutes)
 */
function isTokenExpired(token, bufferMinutes = 5) {
  if (!token) return true;

  const payload = parseJwt(token);
  if (!payload || !payload.exp) return true;

  const expirationTime = payload.exp * 1000; // Convert to milliseconds
  const bufferTime = bufferMinutes * 60 * 1000;
  const now = Date.now();

  return now >= (expirationTime - bufferTime);
}

/**
 * Get time until token expires (in milliseconds)
 */
function getTokenTimeRemaining(token) {
  if (!token) return 0;

  const payload = parseJwt(token);
  if (!payload || !payload.exp) return 0;

  const expirationTime = payload.exp * 1000;
  const remaining = expirationTime - Date.now();

  return Math.max(0, remaining);
}

/**
 * Subscribe to token refresh completion
 */
function subscribeToRefresh(callback) {
  refreshSubscribers.push(callback);
}

/**
 * Notify all subscribers that token has been refreshed
 */
function onTokenRefreshed(newToken) {
  refreshSubscribers.forEach(callback => callback(newToken));
  refreshSubscribers = [];
}

/**
 * Notify all subscribers that token refresh failed
 */
function onRefreshFailed(error) {
  refreshSubscribers.forEach(callback => callback(null, error));
  refreshSubscribers = [];
}

/**
 * Acquire a fresh token, with retry logic
 */
async function acquireToken(forceRefresh = false) {
  const myMSALObj = await getMsalInstance();
  const accounts = myMSALObj.getAllAccounts();

  if (accounts.length === 0) {
    console.warn('[TokenService] No accounts found, user needs to login');
    throw new Error('NO_ACCOUNTS');
  }

  const request = {
    ...tokenRequest,
    account: accounts[0],
    forceRefresh: forceRefresh
  };

  try {
    // Try silent token acquisition first
    const response = await myMSALObj.acquireTokenSilent(request);
    console.log('[TokenService] Token acquired silently');
    return response.accessToken;
  } catch (error) {
    console.warn('[TokenService] Silent token acquisition failed:', error.message);

    if (error instanceof InteractionRequiredAuthError) {
      // Need user interaction - this will redirect
      console.log('[TokenService] Interaction required, redirecting...');
      try {
        await myMSALObj.acquireTokenRedirect(request);
        // This won't return as it redirects
        return null;
      } catch (redirectError) {
        console.error('[TokenService] Redirect failed:', redirectError);
        throw redirectError;
      }
    }

    // For other errors, try force refresh once
    if (!forceRefresh) {
      console.log('[TokenService] Retrying with force refresh...');
      return acquireToken(true);
    }

    throw error;
  }
}

/**
 * Refresh the token and update the store
 * Handles concurrent refresh requests
 */
async function refreshToken() {
  // If already refreshing, wait for that to complete
  if (isRefreshing) {
    console.log('[TokenService] Token refresh already in progress, waiting...');
    return new Promise((resolve, reject) => {
      subscribeToRefresh((token, error) => {
        if (error) reject(error);
        else resolve(token);
      });
    });
  }

  isRefreshing = true;

  try {
    const newToken = await acquireToken(true);

    if (newToken) {
      store.state.aToken = newToken;
      console.log('[TokenService] Token refreshed and stored');
      onTokenRefreshed(newToken);
      return newToken;
    }

    throw new Error('TOKEN_REFRESH_FAILED');
  } catch (error) {
    console.error('[TokenService] Token refresh failed:', error);
    onRefreshFailed(error);
    throw error;
  } finally {
    isRefreshing = false;
  }
}

/**
 * Get a valid token, refreshing if necessary
 * This function serializes token requests to prevent race conditions
 */
async function getValidToken() {
  const currentToken = store.state.aToken;

  console.log('[TokenService] getValidToken called:', {
    hasToken: !!currentToken,
    tokenLength: currentToken ? currentToken.length : 0,
    isExpired: currentToken ? isTokenExpired(currentToken) : 'no token',
    isRefreshingNow: isRefreshing
  });

  if (!currentToken || isTokenExpired(currentToken)) {
    console.log('[TokenService] Token is expired or missing, refreshing...');
    return refreshToken();
  }

  console.log('[TokenService] Token is valid, returning existing token');
  return currentToken;
}

/**
 * Check if user is authenticated
 */
async function isAuthenticated() {
  const myMSALObj = await getMsalInstance();
  const accounts = myMSALObj.getAllAccounts();
  return accounts.length > 0;
}

/**
 * Force user to re-authenticate
 */
async function forceReauthenticate() {
  const myMSALObj = await getMsalInstance();

  try {
    // Clear the token from store
    store.state.aToken = null;

    // Redirect to login
    await myMSALObj.loginRedirect({
      ...tokenRequest,
      prompt: 'login'
    });
  } catch (error) {
    console.error('[TokenService] Force re-authenticate failed:', error);
    throw error;
  }
}

/**
 * Force re-consent to get all scopes
 * Use this when user gets 403 errors that might be due to missing scopes
 */
async function forceReconsent() {
  const myMSALObj = await getMsalInstance();

  try {
    console.log('[TokenService] Forcing re-consent to get all scopes...');

    // Clear cached tokens
    store.state.aToken = null;

    // Clear MSAL cache
    const accounts = myMSALObj.getAllAccounts();
    if (accounts.length > 0) {
      // Sign out silently to clear cache
      await myMSALObj.logoutRedirect({
        account: accounts[0],
        postLogoutRedirectUri: window.location.origin + '/?reconsent=true'
      });
    } else {
      // No account, just redirect to login with consent
      await myMSALObj.loginRedirect({
        ...tokenRequest,
        prompt: 'consent'
      });
    }
  } catch (error) {
    console.error('[TokenService] Force re-consent failed:', error);
    throw error;
  }
}

/**
 * Acquire token with forced consent (for 403 errors due to missing scopes)
 */
async function acquireTokenWithConsent() {
  const myMSALObj = await getMsalInstance();
  const accounts = myMSALObj.getAllAccounts();

  if (accounts.length === 0) {
    throw new Error('NO_ACCOUNTS');
  }

  const request = {
    ...tokenRequest,
    account: accounts[0],
    prompt: 'consent'
  };

  try {
    // Try popup first for better UX
    const response = await myMSALObj.acquireTokenPopup(request);
    console.log('[TokenService] Token acquired with consent popup');
    store.state.aToken = response.accessToken;
    return response.accessToken;
  } catch (error) {
    console.warn('[TokenService] Popup consent failed, trying redirect:', error);
    // Fallback to redirect
    await myMSALObj.acquireTokenRedirect(request);
    return null;
  }
}

export default {
  getMsalInstance,
  isTokenExpired,
  getTokenTimeRemaining,
  acquireToken,
  refreshToken,
  getValidToken,
  isAuthenticated,
  forceReauthenticate,
  forceReconsent,
  acquireTokenWithConsent,
  parseJwt
};
